import { Select, Surface } from '@chopchop-se/ui';
import { useQuery } from '@tanstack/react-query';
import { DELIVERY_CALENDAR_WINDOW_DAYS } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../../components/PageHeader';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { ORDER_QUERY_KEYS, deliveryScheduleApi, portalApi } from '../api';
import { DeliveryCalendar } from '../components/DeliveryCalendar';

/**
 * "Leveranskalender" — the read-only view of what a unit can order and when.
 * Same component and same server-computed date set the portal uses, so what an
 * admin sees here is exactly what the restaurant sees.
 */
export function DeliveryCalendarPage(): ReactNode {
  const { t } = useTranslation(['salesOrders', 'common']);
  const [restaurantId, setRestaurantId] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const restaurantsQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurants(),
    queryFn: () => portalApi.restaurants(),
  });

  const datesQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.deliveryDates(restaurantId, []),
    queryFn: () => portalApi.deliveryDates(restaurantId, []),
    enabled: restaurantId.length > 0,
  });

  const configQuery = useQuery({
    queryKey: ORDER_QUERY_KEYS.restaurantDelivery(restaurantId),
    queryFn: () => deliveryScheduleApi.forRestaurant(restaurantId),
    enabled: restaurantId.length > 0,
  });

  useLiveQuery(['delivery-schedules'], ['delivery-schedules']);

  return (
    <>
      <PageHeader
        title={t('salesOrders:calendar.title')}
        subtitle={t('salesOrders:calendar.subtitle', { days: DELIVERY_CALENDAR_WINDOW_DAYS })}
      />

      <div className="mb-4 max-w-sm">
        <Select
          label={t('salesOrders:calendar.restaurant')}
          placeholder={t('salesOrders:schedule.form.restaurantPlaceholder')}
          value={restaurantId}
          onChange={(event) => {
            setRestaurantId(event.target.value);
            setSelected(null);
          }}
          options={(restaurantsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.name }))}
        />
      </div>

      {restaurantId ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Surface className="p-5">
            <DeliveryCalendar
              dates={datesQuery.data?.dates ?? []}
              passed={datesQuery.data?.passed ?? []}
              scheduleMissing={datesQuery.data?.scheduleMissing ?? false}
              loading={datesQuery.isLoading}
              value={selected}
              onChange={setSelected}
            />
          </Surface>

          <Surface className="p-5">
            <h2 className="mb-3 font-medium">{t('salesOrders:schedule.title')}</h2>
            {(configQuery.data?.schedules.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-muted">{t('salesOrders:schedule.summary.none')}</p>
            ) : (
              <ul className="flex flex-col gap-2 text-sm">
                {configQuery.data?.schedules.map((schedule) => (
                  <li key={schedule.id} className="rounded-lg border border-surface-border px-3 py-2">
                    <p className="font-medium">
                      {schedule.categoryName ?? t('salesOrders:schedule.defaultCategory')}
                    </p>
                    <p className="text-xs text-ink-muted">
                      {t('salesOrders:schedule.cell.delivery')}:{' '}
                      {[schedule.deliveryDay1, schedule.deliveryDay2]
                        .filter((d): d is number => d !== null)
                        .map((d) => t(`salesOrders:weekdayShort.${d}`))
                        .join(', ')}{' '}
                      · {t('salesOrders:schedule.cell.due')}:{' '}
                      {[
                        schedule.dueDay1 !== null
                          ? `${t(`salesOrders:weekdayShort.${schedule.dueDay1}`)}${schedule.dueTime1 ? ` ${schedule.dueTime1}` : ''}`
                          : null,
                        schedule.dueDay2 !== null
                          ? `${t(`salesOrders:weekdayShort.${schedule.dueDay2}`)}${schedule.dueTime2 ? ` ${schedule.dueTime2}` : ''}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  </li>
                ))}
              </ul>
            )}

            {(configQuery.data?.customDates.length ?? 0) > 0 ? (
              <>
                <h3 className="mb-2 mt-4 font-medium">{t('salesOrders:schedule.custom.title')}</h3>
                <ul className="flex flex-col gap-1 text-sm">
                  {configQuery.data?.customDates.map((custom) => (
                    <li key={custom.id} className="font-mono text-xs">
                      {custom.changedDate} → {custom.newDate}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </Surface>
        </div>
      ) : (
        <p className="text-sm text-ink-muted">{t('salesOrders:calendar.selectRestaurant')}</p>
      )}
    </>
  );
}
